import React, { useReducer } from 'react'
import useForm from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import Phone from '../../assets/phone.svg'
import { t } from '../../../common/i18n'
import Size from '../../utils/media-queries-setup'
import Button from '../buttons/Button'
import CheckBoxYellowCesta from '../checkboxYellowCesta/CheckBoxYellowCesta'
import Error from '../error/Error'
import FormError from '../FormError/FormError'
import { validatePhone } from '../../../specific/helpers/validation/validationPhone'
import styles from './teLlamamosForm.module.scss'
import logic from '../../logic'
import * as yup from 'yup'
import dataLayer from '../../helpers/functions/dataLayer'
import Turnstile, { useTurnstile } from "react-turnstile"

const initialState = {
  globalError: null as string | null,
  success: false as boolean,
  loading: false as boolean,
  data: { phone: '', checkboxPrivacy: false , token: ''} as PhoneForm,
}
type State = typeof initialState

const CallbackSchema = yup.object().shape({
  phone: yup
    .string()
    .test('test-phone_number', '${message}', validatePhone)
    .required(
      t('validations.register.required', { label: t('formLabels.phone') })
    ),
  checkboxPrivacy: yup
    .boolean()
    .oneOf([true], t('validations.register.accept_terms')),
})

interface PhoneForm {
  phone: string
  checkboxPrivacy: boolean
  token: string
}

interface SetCallbackAction {
  type: 'SET_CALLBACK'
}

interface SetCallbackErrorsAction {
  type: 'SET_CALLBACK_ERRORS'
}

interface SetCallbackOkAction {
  type: 'SET_CALLBACK_OK'
}

interface SetCallbackFailedAction {
  type: 'SET_CALLBACK_FAILED'
  payload: {
    error: string
  }
}

interface SetCallbackToken {
  type: 'SET_TOKEN'
  payload: {
    token: string
  }
}


type Action =
  | SetCallbackAction
  | SetCallbackErrorsAction
  | SetCallbackFailedAction
  | SetCallbackOkAction
  | SetCallbackToken

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_CALLBACK':
      return {
        ...state,
        loading: true,
      }

    case 'SET_CALLBACK_ERRORS':
      return {
        ...state,
        loading: false,
      }

    case 'SET_CALLBACK_OK':
      return {
        ...state,
        loading: false,
        success: true,
        globalError: null,
      }
    case 'SET_CALLBACK_FAILED':
      return {
        ...state,
        loading: false,
        success: false,
        globalError: action.payload.error,
      }

    case 'SET_TOKEN':
      return {
        ...state,
        data: {
          ...state.data,
          token: action.payload.token,
        }
      }
  }
}

interface Props {
  setLoading: (status: boolean) => void
  setSuccess: (status: boolean) => void
  taller?: string
}

const TeLlamamosForm = ({ taller, setLoading, setSuccess }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    register,
    handleSubmit,
    errors,
    setError,
    clearError,
    triggerValidation,
  } = useForm<PhoneForm>({
    validationSchema: CallbackSchema,
  })

  const device = Size.useMedia({
    mobile: 'MOBILE',
    tablet: 'TABLET',
    desktop: 'DESKTOP',
  }) as 'MOBILE' | 'TABLET' | 'DESKTOP'

  const onSubmit = (data: PhoneForm) => {
    const { phone } = data
    dataLayer.push({
      event: 'callback',
      action: 'conversion',
    })
    const path = window.location.pathname
    const userAgent = navigator.userAgent
    dispatch({
      type: 'SET_CALLBACK',
    })
    setLoading(true)
    logic
      .addCallback(phone, path, device, userAgent, taller, state.data.token)
      .then((res) => {
        if (res.errors) {
          res.errors.forEach((e) => {
            setError(e.field, e.type, t(e.message))
            dispatch({
              type: 'SET_CALLBACK_ERRORS',
            })
            setLoading(false)
          })
        } else {
          dispatch({
            type: 'SET_CALLBACK_OK',
          })
          setLoading(false)
          setSuccess(true)
        }
      })
      .catch(({ message }) => {
        dispatch({
          type: 'SET_CALLBACK_FAILED',
          payload: {
            error: message,
          },
        })
        setLoading(false)
        setSuccess(false)
      })
  }

  const turnstile = useTurnstile()

  const onVerify = (token: string) => {
    dispatch({
      type: 'SET_TOKEN',
      payload: {
        token: token,
      }
    })
  }

  return (
    <>
      <div className={styles.modal_wrapper_step1}>
        <div className={styles.horarios}>
          <FormattedMessage id="funnel.horario1" />
          <FormattedMessage id="funnel.horario2" />
          <FormattedMessage id="funnel.horario3" />
        </div>
        <form
          className={`${styles.form} ${
            state.loading || state.success ? styles.hidden : ''
          }`}
          onBlur={(e) => {
            const target = e.target as HTMLFormElement
            // Revalidar cualquier campo cuando cambia su valor
            clearError(target.name as keyof PhoneForm)
            triggerValidation({
              name: target.name as keyof PhoneForm,
            })
          }}
          onSubmit={(e) => {
            // Validar todo el formulario antes de tratar de enviar
            triggerValidation()
            handleSubmit(onSubmit)(e)
            turnstile.reset()
          }}>
          <div className={styles.inputPhone}>
            <Phone className={styles.phone_icon} />
            <input
              className={styles.input}
              name="phone"
              ref={register}
              type="text"
              placeholder={t('funnel.telefono')}
              defaultValue={state.data.phone}
            />
          </div>
          <div className={styles.phone_error}>
            <FormError errors={errors} field="phone" />
          </div>
          <CheckBoxYellowCesta
            label={
              <div className={styles.label_title}>
                <FormattedMessage id="footer.accept2" />{' '}
              </div>
            }
            name="checkboxPrivacy"
            refe={register}
            checked={state.data.checkboxPrivacy}
          />
          <FormError errors={errors} field="checkboxPrivacy" />
          <div className={styles.button_wrapper}>
            <Button type="submit">
              <FormattedMessage id="funnel.llamame" />
            </Button>
          </div>
          {state.globalError && <Error message={state.globalError}></Error>}
        </form>
      </div>
      <Turnstile
        refreshExpired='auto'
        theme='light'
        sitekey={process.env.GATSBY_TURNSTILE_KEY}
        onVerify={onVerify}
      />
    </>
  )
}

export default TeLlamamosForm
